'use client';

import useRollbar from '@/hooks/useRollbar';
import { useStore } from '@/stores/store';
import { env } from '@/env';
import { getFirebaseMessaging, initMessaging } from '@/utils/firebase';
import { isMobile, isPWA } from '@/utils/platform';
import { useLocalStorage } from '@mantine/hooks';
import { NotificationData, notifications } from '@mantine/notifications';
import { MessagePayload } from 'firebase/messaging';
import localforage from 'localforage';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { SILENT_NOTIFICATION_TYPE } from '@/utils/notificationConstants';

const ACK_ENDPOINT = new URL(
  '/api/auth/notificationToken/ack',
  env.NEXT_PUBLIC_SERVER_URL,
).toString();
const ACK_DELAY_MS = 2000;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function sendForegroundPushAck(
  payload: MessagePayload,
  rollbar: ReturnType<typeof useRollbar>,
) {
  const userId = payload.data?.userId;
  const token = payload.data?.token;
  if (!userId || !token) return;

  try {
    await sleep(ACK_DELAY_MS);
    const response = await fetch(ACK_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ userId, token }),
    });

    if (!response.ok) {
      console.warn('[push-ack] Foreground ACK failed', { status: response.status, userId });
    }
  } catch (error) {
    rollbar.error('Failed to send foreground push ACK', error as Error);
  }
}

export function useFirebaseMessaging() {
  const rollbar = useRollbar();
  const router = useRouter();
  const [token, setToken] = useState<string | null>(null);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');

  useEffect(() => {
    if (typeof window !== 'undefined' && isSupported()) {
      setPermissionStatus(Notification.permission);
    }
  }, []);
  const user = useStore((store) => store.user);
  const notificationPlaySound = user?.flags?.notificationPlaySound ?? false;
  const [supported, setSupported] = useLocalStorage<boolean | null>({
    key: 'FCM_SUPPORTED',
    defaultValue: null,
  });

  async function requestPermission() {
    if (!supported) return;

    importFirebaseMessaging()
      .then(async (messaging) => {
        if (!messaging) return;
        if ('Notification' in window && (await messaging.isSupported())) {
          const permission = await Notification.requestPermission();
          setPermissionStatus(permission);
        }
      })
      .catch((error) => {
        rollbar.error('Mensagem', error);
      });
  }

  useEffect(() => {
    importFirebaseMessaging()
      .then((messaging) => {
        if (!messaging) {
          // Browser without the messaging
          setSupported(false);
          return;
        }
        messaging
          .isSupported()
          .then(setSupported)
          .catch((error) => rollbar.error('Falha ao verificar suporte a notificações', error));
      })
      .catch((error) =>
        rollbar.error('useFirebaseMessaging - Falha ao importar firebase messaging', error),
      );
  }, []);

  useEffect(() => {
    if (permissionStatus === 'granted' && supported) {
      initMessaging()
        .then((token) => {
          setToken(token || null);
        })
        .catch((error) => {
          rollbar.error('Falha ao inicializar firebase cloud messaging', error);
        });
    } else {
      localforage.removeItem('FCM_TOKEN');
    }
  }, [permissionStatus, supported]);

  useEffect(() => {
    if (!(permissionStatus === 'granted' && supported && user)) return;

    let unsubscribe: (() => void) | undefined;

    const setup = async () => {
      const messaging = await importFirebaseMessaging();
      if (!messaging) {
        if (!isMobile.iOS()) rollbar.warn('Messaging returning undefined', { messaging });
        return;
      }
      const firebaseMessaging = await getFirebaseMessaging();
      if (!firebaseMessaging) return;

      unsubscribe = messaging.onMessage(firebaseMessaging, (payload) => {
        if (payload.data?.type === SILENT_NOTIFICATION_TYPE) {
          void sendForegroundPushAck(payload, rollbar);
          return;
        }

        // Tab is visible but not focused: hand off to the service worker so it
        // displays the OS notification (message -> showNotification in sw.ts).
        // When truly minimized (visibilityState === 'hidden') this onMessage
        // listener never fires; that case is handled by onBackgroundMessage in sw.ts.
        if (!document.hasFocus() && document.visibilityState === 'visible') {
          navigator.serviceWorker.ready.then((registration) => {
            registration.active?.postMessage({
              messageType: 'background-notification',
              payload,
              userPrefs: { notificationPlaySound },
            });
          });
          return;
        }

        void sendForegroundPushAck(payload, rollbar);
        const url = payload.data?.link || '';

        if (window.location.href.includes('/falar') || window.location.pathname === url) {
          return;
        }

        if (notificationPlaySound) {
          const audio = new Audio('/sounds/new-message-sound.mp3');
          audio.play().catch((error) => {
            rollbar.error('Falha ao tocar som de notificação', error);
          });
        }

        const id = notifications.show(
          generateNotificationConfig(payload, (destiny) => {
            router.push(destiny);
            notifications.hide(id);
          }),
        );
      });
    };

    setup().catch((error) => rollbar.error('Error on onMessage listener', error as Error));

    return () => {
      unsubscribe?.();
    };
  }, [permissionStatus, supported, user]);

  useEffect(() => {
    if (isMobile.iOS() && !isPWA()) return; // Notification is not available on iOS without the PWA installed

    const handleFirebaseMessage = (event: MessageEvent) => {
      try {
        if (event.data.messageType === 'notification-clicked') {
          const payload: MessagePayload = event.data;
          if (payload.data?.link) {
            router.push(payload.data.link);
          }
        }

        if (event.data.messageType === 'play-custom-notification-sound') {
          const audio = new Audio('/sounds/new-message-sound.mp3');
          audio.play().catch((error) => {
            rollbar.error('Falha ao tocar som de notificação', error);
          });
        }
      } catch (error) {
        rollbar.error('Error on handling Firebase notification clicked event', error as Error);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleFirebaseMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleFirebaseMessage);
  }, [router]);

  return [requestPermission, { permissionStatus, token, supported }] as const;
}

export async function importFirebaseMessaging() {
  // Firebase messaging can only be imported on the client side
  if (typeof window === 'undefined') return;

  // Firebase messaging can only be imported on supported browsers
  if (!isSupported()) return;

  const messaging = await import('firebase/messaging');
  return messaging;
}

export function isSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

// Also used on /src/views/Storybook/components/StorybookNotification.tsx
export const generateNotificationConfig = (
  payload: MessagePayload,
  onClick: (url: string) => void,
): NotificationData => {
  return {
    title: payload.data?.title,
    message: payload.data?.body,
    autoClose: false,
    onClick: (event) => {
      // Checking if user clicked on the close button
      if (!(event.target as HTMLElement).closest('.mantine-Notification-closeButton')) {
        onClick(payload.data?.link || '/');
      }
    },
    classNames: {
      root: 'text-white cursor-pointer bg-primary-600 hover:bg-primary-400 transition-colors',
      title: 'text-white font-bold',
      description: 'text-white',
      closeButton: 'text-white hover:bg-gray-50 hover:text-primary-700 transition-colors',
    },
  };
};
